import clsx from "clsx";
import React, { FunctionComponent, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { navigate } from "wouter/use-location";

import { Language, useLanguage } from "../../../i18n/useLanguage";
import { HashLink } from "../../../navigation/HashLink";
import { useGetPartPackagesByDealerId } from "../../../queries/useGetPartPackagesByDealerId";
import {
  PackageItem,
  PackagePartItem,
} from "../../../queries/useSearchPartPackagesByVehicle";
import { useUrl } from "../../../stores/hooks/useUrl";
import { createQueryStringToggleHook } from "../../../stores/useQueryState";
import { Button, RoundButton } from "../../../ui/Button/Button";
import { KeyValue } from "../../../ui/KeyValue/KeyValue";
import { LinearProgress } from "../../../ui/LinearProgress";
import { PriceDisplay } from "../../../ui/PriceDisplay/PriceDisplay";
import { ChevronRightIcon } from "../../../ui/icons/ChevronRightIcon";
import { CopyIcon } from "../../../ui/icons/CopyIcon";
import { PenIcon } from "../../../ui/icons/PenIcon";
import { TrashIcon } from "../../../ui/icons/TrashIcon";
import { getPartName } from "../../../util/getPartName";
import { NoData } from "../../NoData/NoData";
import { CopyPackageDialog } from "../CopyPackageDialog/CopyPackageDialog";
import { DeletePackageDialog } from "../DeletePackageDialog/DeletePackageDialog";
import { EditPackageDialog } from "../EditPackageDialog/EditPackageDialog";
import { useVehicleMappingCriteria } from "../useVehicleMappingCriteria";

const useRowState = createQueryStringToggleHook("package-content", []);

export const DealerPackagesList: FunctionComponent = () => {
  const { data, isLoading, mutate } = useGetPartPackagesByDealerId();
  const [language] = useLanguage();
  const { t } = useTranslation();
  const { hash } = useUrl();
  const [, setVehicleMappingCriteria] = useVehicleMappingCriteria();
  const packageToEdit = data?.partPackages.find(
    (item) =>
      item?.partDetails.primaryPart.itemId ===
      hash.replace("#edit-package-", ""),
  );
  const packageToCopy = data?.partPackages.find(
    (item) =>
      item?.partDetails.primaryPart.itemId ===
      hash.replace("#copy-package-", ""),
  );
  const packageToDelete = data?.partPackages.find(
    (item) =>
      item?.partDetails.primaryPart.itemId ===
      hash.replace("#delete-package-", ""),
  );
  const closeDialog = useCallback(() => {
    const nextLocation = `${window.location.pathname}${window.location.search}`;
    setVehicleMappingCriteria("");
    navigate(nextLocation, { replace: true });
  }, []);
  return (
    <>
      {isLoading && <LinearProgress className="h-0.5" />}
      {data?.partPackages.map((part) => (
        <PackageRow
          key={part.partDetails.primaryPart.itemId}
          language={language}
          part={part}
        />
      ))}
      {!data?.partPackages.length && !isLoading && (
        <NoData>{t("packageEditor.noData")}</NoData>
      )}
      <EditPackageDialog
        onClose={closeDialog}
        selectedPackage={packageToEdit}
      />
      <CopyPackageDialog onClose={closeDialog} packageToCopy={packageToCopy} />
      <DeletePackageDialog
        onClose={closeDialog}
        packageToDelete={packageToDelete}
        reload={mutate}
      />
    </>
  );
};

const PackageRow: FunctionComponent<{
  language: Language;
  part: PackagePartItem;
}> = ({ language, part }) => {
  const { t } = useTranslation();
  const [expandedRows, { toggle }] = useRowState();
  const isExpanded = expandedRows.includes(part.partDetails.primaryPart.itemId);
  const partName = getPartName(part, language);
  const tireManufacturerImage =
    part.partTire?.partDetails.primaryPart.tireManufacturerImage;
  return (
    <>
      <div
        className={clsx(
          "text-xs xl:text-sm text-ink-2 border border-edge rounded-panel-section overflow-clip",
          "divide-y divide-edge",
        )}
      >
        <div
          className={clsx(
            "grid grid-cols-24 gap-2 xl:gap-3 px-4 py-2 items-center",
            isExpanded && "bg-paper-2",
          )}
        >
          <div className="col-span-8 xl:pr-4">
            <div className="text-ink font-semibold">{partName}</div>
            <div className="text-xs">{part.partDetails.primaryPart.itemId}</div>
          </div>
          <div className="col-span-4 xl:col-span-5 -ml-4">
            {tireManufacturerImage && (
              <img
                alt="Manufacturer logo"
                className="max-h-6"
                src={tireManufacturerImage}
              />
            )}
          </div>
          <div className="col-span-4 xl:col-span-5">
            {part.partDetails.primaryPart.packagePartNumber}
          </div>
          <div className="col-span-2 text-right">
            <PriceDisplay>
              {
                part.partPricing.primaryPart.price["1"]?.contextList[0]
                  ?.displayPrice
              }
            </PriceDisplay>
          </div>
          <div className="col-span-6 xl:col-span-4 pl-2 xl:pl-8 flex items-center justify-between gap-2">
            <HashLink
              hash={`#edit-package-${part.partDetails.primaryPart.itemId}`}
            >
              <RoundButton
                aria-label="editPackage"
                className="lg:hidden"
                title={t("packageEditor.edit")}
              >
                <PenIcon />
              </RoundButton>
              <Button
                aria-label="editPackage"
                className="text-xs border border-edge-2 hidden lg:flex"
              >
                <PenIcon />
                {t("packageEditor.edit")}
              </Button>
            </HashLink>
            <HashLink
              hash={`#copy-package-${part.partDetails.primaryPart.itemId}`}
            >
              <RoundButton
                aria-label="copyPackage"
                title={t("packageEditor.copyPackage")}
              >
                <CopyIcon />
              </RoundButton>
            </HashLink>
            <HashLink
              hash={`#delete-package-${part.partDetails.primaryPart.itemId}`}
            >
              <RoundButton
                aria-label="deletePackage"
                title={t("packageEditor.deletePackage")}
              >
                <TrashIcon />
              </RoundButton>
            </HashLink>
            <RoundButton
              onClick={(): void => {
                toggle([part.partDetails.primaryPart.itemId]);
              }}
              aria-label="togglePackageDetails"
              className="ml-auto"
              disabled={!part.partDetails.primaryPart.packageItem.length}
            >
              <ChevronRightIcon
                className={clsx(
                  "transform transition-transform",
                  isExpanded ? "-rotate-90" : "rotate-90",
                )}
              />
            </RoundButton>
          </div>
        </div>
        {isExpanded &&
          part.partDetails.primaryPart.packageItem.map((packageItem, key) => (
            <ProductRow
              index={key + 1}
              key={packageItem.itemId}
              language={language}
              packageItem={packageItem}
            />
          ))}
      </div>
    </>
  );
};

const ProductRow: FunctionComponent<{
  index: number;
  language: Language;
  packageItem: PackageItem;
}> = ({ index, language, packageItem }) => {
  return (
    <div className="flex items-center gap-4 px-4 py-2">
      <strong>{String(index).padStart(2, "0")}</strong>
      <KeyValue className="col-span-8 mr-auto">
        <>{packageItem.itemTypeDescription[language]}</>
        <strong>{packageItem.itemTitle[language]}</strong>
      </KeyValue>
      <KeyValue className="col-span-4">
        <>Quantity:</>
        <>{packageItem.itemQuantity}</>
      </KeyValue>
    </div>
  );
};
